<template>
    <vue-autosuggest
        ref="autosuggest"
        v-model="selectedItem"
        :suggestions="filteredOptions"
        :get-suggestion-value="(s) => s.item.nameWithCrs"
        :limit="5"
        :input-props="{'class': `station-input ${extraClasses}`, placeholder, ref: 'input'}"
        @keydown.tab="onTab"
        @input="onInputChange"
        @focus="onFocus"
        @selected="onSelected"
        @blur="blur"
    >
        <template slot-scope="{suggestion}">
            <span
                class="autosuggest__results-item-tabtarget w-100 h-100" tabindex="-1"
            >
                {{ suggestion.item.nameWithCrs }}
            </span>
        </template>
    </vue-autosuggest>
</template>

<script>
import { VueAutosuggest } from 'vue-autosuggest-fix-touch';
import debounce from 'lodash-es/debounce';

export default {
    name: 'StationPicker',
    components: { VueAutosuggest },
    props: {
        startValue: {
            type: String,
            default: null,
            required: false,
        },
        placeholder: {
            type: String,
            default: 'Station...',
            required: false,
        },
        extraClasses: {
            type: String,
            required: false,
            default: '',
        },
    },
    data() {
        return {
            filteredOptions: [],
            selectedItem: null,
            currentText: '',
            isEmpty: true,
            fetchPromise: null,
        };
    },
    watch: {
        startValue(newValue) {
            this.selectedItem = newValue;
            this.isEmpty = false;
        },
    },
    mounted() {
        this.throttledFunc = debounce(this.updateFor, 50);
        if (this.startValue) {
            this.selectedItem = this.startValue;
            this.isEmpty = false;
        }
    },
    methods: {
        onTab() {
            if (this.isEmpty) {
                this.$emit('emptied');
                return;
            }

            const {
                listeners,
                setCurrentIndex,
                setChangeItem,
                getItemByIndex,
            } = this.$refs.autosuggest;

            if (this.fetchPromise && this.fetchPromise.isPending()) {
                this.fetchPromise.then(() => {
                    this.onTab();
                });
                return;
            }
            setCurrentIndex(0);
            const item = getItemByIndex(this.$refs.autosuggest.currentIndex);
            if (!item) {
                return;
            }
            setChangeItem(item, true);
            this.$refs.autosuggest.loading = true;
            listeners.selected(true);
        },
        onFocus() {
            setTimeout(() => {
                this.$refs.autosuggest.$refs.input.select();
            }, 0);
        },
        blur(ev) {
            if (ev.relatedTarget !== null && ev.relatedTarget.classList.contains('autosuggest__results-item-tabtarget')) {
                return;
            }

            if (this.fetchPromise && this.fetchPromise.isPending()) {
                this.fetchPromise.then(() => {
                    this.blur(ev);
                });
                return;
            }

            if (this.isEmpty) {
                this.$emit('emptied');
                return;
            }

            const {
                setCurrentIndex,
                listeners,
                setChangeItem,
                computedSections,
                normalizeItem,
                didSelectFromOptions,
            } = this.$refs.autosuggest;

            if (didSelectFromOptions) {
                return;
            }

            const section = computedSections[0];

            if (section === undefined) {
                return;
            }

            const item = section.data[section.start_index];

            if (item === undefined) {
                return;
            }

            // manually construct our own vueAutosuggestItem
            const vueAutosuggestItem = normalizeItem(
                section.name,
                section.type,
                undefined,
                undefined,
                item,
            );
            setCurrentIndex(section.start_index, section.end_index, false);
            setChangeItem(vueAutosuggestItem, true);
            this.$refs.autosuggest.loading = true;
            listeners.selected(true);
        },
        onInputChange(query) {
            if (query === '' || query === undefined) {
                this.isEmpty = true;
                this.filteredOptions = [{
                    data: [],
                }];
                this.currentText = '';
                return;
            }
            this.isEmpty = false;
            this.currentText = query;
            this.throttledFunc(query);
        },
        updateFor(query) {
            // Ported from Typescript - (C) Trainsplit.com Ltd 2019
            this.fetchPromise = this.makeQueryablePromise(
                fetch(`/api/StationAutoComplete?query=${window.encodeURIComponent(query)}`)
                    .then((r) => r.json())
                    .then((response) => {
                        if (response.originalQuery !== this.currentText) {
                            return;
                        }
                        const filteredData = response.data;
                        this.filteredOptions = [{
                            data: filteredData.slice(0, 5),
                        }];
                    }),
            );
        },
        makeQueryablePromise(promise) {
            // Don't modify any promise that has been already modified.
            if (promise.isResolved) return promise;

            // Set initial state
            let isPending = true;
            let isRejected = false;
            let isFulfilled = false;

            // Observe the promise, saving the fulfillment in a closure scope.
            const result = promise.then(
                (v) => {
                    isFulfilled = true;
                    isPending = false;
                    return v;
                },
                (e) => {
                    isRejected = true;
                    isPending = false;
                    throw e;
                },
            );

            result.isFulfilled = () => isFulfilled;
            result.isPending = () => isPending;
            result.isRejected = () => isRejected;
            return result;
        },
        onSelected(item) {
            if (item) {
                this.$emit('pick', {
                    key: item.item.uic,
                    name: item.item.nameWithCrs,
                    shortName: item.item.name,
                });
                this.selectedItem = item.item.nameWithCrs;
            }
        },
    },
};
</script>
